import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyReveal } from "./KeyReveal";
import { Plus, X, Copy, Check, Trash2, Printer } from "lucide-react";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { ApiError } from "@finora/api-client";
import type { BusinessLocationOptionDto, PrintAgentListItemDto } from "@finora/types";
import { api } from "../../lib/api";

/**
 * "Podešavanja -> Print Agent" - konsolidovano 04.10.2026 (vidi razgovor:
 * "stavi da sve bude u jednom a ne ovako, jer ako imamo vise licenci sve
 * ce biti razbacano nece se korisnik snaci"). Ranije: tri odvojene ravne
 * liste (ključevi / agenti / automatska štampa po lokaciji) - sada JEDNA
 * kartica PO LICENCNOM KLJUČU, koja grupiše agente koji su se tim ključem
 * prijavili, i za svaki agent - dodelu lokacije i automatsku štampu ZA TU
 * lokaciju, sve na jednom mestu.
 *
 * Takođe: dodata podrška za 58mm traku (pitanje "a zasto nema 58 mm") -
 * PDF servis je odavno podržavao thermal80/thermal58/a4, ali automatska
 * štampa je do sada uvek pretpostavljala 80mm.
 */

type AutoPrintDraft = {
  enabled: boolean;
  printerRole: "thermal" | "a4";
  thermalWidth: "80mm" | "58mm";
  copies: number;
};

function draftFromLocation(loc: BusinessLocationOptionDto): AutoPrintDraft {
  return {
    enabled: !!loc.autoPrintEnabled,
    printerRole: (loc.autoPrintPrinterRole as "thermal" | "a4" | null) ?? "thermal",
    thermalWidth: (loc.autoPrintThermalWidth as "80mm" | "58mm" | null) ?? "80mm",
    copies: loc.autoPrintCopies ?? 1,
  };
}

export function NewKeyRevealModal({
  rawKey,
  onClose,
  title = "Novi licencni ključ",
  hint,
}: {
  rawKey: string;
  onClose: () => void;
  title?: string;
  hint?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        </div>
        <div className="space-y-3 px-5 py-5">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Ovo je jedini put da vidite pun ključ - sačuvajte ga sada, posle se ne može ponovo
            prikazati. {hint ?? "Unesite ga u Print Agent na Windows računaru (Licenca -> Licencni ključ)."}
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-app-bg px-3 py-2 font-mono text-sm break-all">
            {rawKey}
          </div>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(rawKey).catch(() => {
                const el = document.createElement("textarea");
                el.value = rawKey;
                document.body.appendChild(el);
                el.select();
                document.execCommand("copy");
                document.body.removeChild(el);
              });
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 text-sm text-brand hover:underline"
          >
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {copied ? "Kopirano" : "Kopiraj ključ"}
          </button>
        </div>
        <div className="flex justify-end border-t border-border px-5 py-4">
          <Button onClick={onClose}>Zatvorio sam ovo negde bezbedno</Button>
        </div>
      </div>
    </div>
  );
}

function NewKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (rawKey: string) => void }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createPrintAgentKey(label || undefined),
    onSuccess: (key) => {
      queryClient.invalidateQueries({ queryKey: ["print-agent-keys"] });
      onCreated(key.key);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Ključ nije napravljen."),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novi licencni ključ</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Oznaka (opciono)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="npr. Radnja Niš"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-400">
              Pomaže da kasnije prepoznate za koji računar/radnju je ključ izdat - ne utiče na
              funkcionisanje agenta.
            </p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button className="flex-1" disabled={create.isPending} onClick={() => create.mutate()}>
            Napravi ključ
          </Button>
        </div>
      </div>
    </div>
  );
}

function AgentAutoPrintControls({
  location,
  draft,
  onChange,
  onSave,
  isSaving,
}: {
  location: BusinessLocationOptionDto;
  draft: AutoPrintDraft;
  onChange: (next: AutoPrintDraft) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-app-bg px-3 py-2">
      <button
        type="button"
        onClick={() => onChange({ ...draft, enabled: !draft.enabled })}
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          draft.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
        }`}
      >
        {draft.enabled ? "Automatska štampa: uključena" : "Automatska štampa: isključena"}
      </button>
      <select
        value={draft.printerRole}
        onChange={(e) => onChange({ ...draft, printerRole: e.target.value as "thermal" | "a4" })}
        className="h-8 rounded-md border border-border px-2 text-xs focus:border-brand focus:outline-none"
      >
        <option value="thermal">Termalni</option>
        <option value="a4">A4</option>
      </select>
      {draft.printerRole === "thermal" && (
        <select
          value={draft.thermalWidth}
          onChange={(e) => onChange({ ...draft, thermalWidth: e.target.value as "80mm" | "58mm" })}
          className="h-8 rounded-md border border-border px-2 text-xs focus:border-brand focus:outline-none"
          title="Širina trake"
        >
          <option value="80mm">80mm</option>
          <option value="58mm">58mm</option>
        </select>
      )}
      <input
        type="number"
        min={1}
        max={20}
        value={draft.copies}
        onChange={(e) => onChange({ ...draft, copies: Number(e.target.value) || 1 })}
        className="h-8 w-16 rounded-md border border-border px-2 text-xs focus:border-brand focus:outline-none"
        title="Broj kopija"
      />
      <Button size="sm" variant="secondary" disabled={isSaving} onClick={onSave}>
        {isSaving ? "Čuvam…" : "Sačuvaj"}
      </Button>
      <span className="text-xs text-slate-400">za lokaciju „{location.name}"</span>
    </div>
  );
}

function AgentRow({
  agent,
  locations,
  draft,
  onDraftChange,
  onSaveAutoPrint,
  isSavingAutoPrint,
}: {
  agent: PrintAgentListItemDto;
  locations: BusinessLocationOptionDto[];
  draft: AutoPrintDraft | undefined;
  onDraftChange: (locationId: string, next: AutoPrintDraft) => void;
  onSaveAutoPrint: (locationId: string, draft: AutoPrintDraft) => void;
  isSavingAutoPrint: boolean;
}) {
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(agent.name);

  const rename = useMutation({
    mutationFn: ({ name }: { name: string }) => api.renamePrintAgent(agent.id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["print-agents"] });
      setRenaming(false);
    },
  });

  const revoke = useMutation({
    mutationFn: () => api.revokePrintAgent(agent.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["print-agents"] }),
  });

  const assignLocation = useMutation({
    mutationFn: (businessLocationId: string | null) => api.assignPrintAgentLocation(agent.id, businessLocationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["print-agents"] });
      queryClient.invalidateQueries({ queryKey: ["business-location-options"] });
    },
  });

  const assignedLocation = locations.find((l) => l.id === agent.business_location_id);

  return (
    <div className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[8rem] font-medium text-slate-800">
          {renaming ? (
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => {
                if (renameValue.trim() && renameValue !== agent.name) {
                  rename.mutate({ name: renameValue.trim() });
                } else {
                  setRenaming(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setRenaming(false);
              }}
              className="h-8 w-32 rounded-md border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          ) : (
            <button
              onClick={() => {
                setRenaming(true);
                setRenameValue(agent.name);
              }}
              className="hover:underline"
              title="Klikni da preimenuješ"
            >
              {agent.name}
            </button>
          )}
        </div>
        <span className="text-sm text-slate-500">{agent.hostname}</span>
        <span className="text-sm text-slate-500">
          {agent.printers.length === 0 ? "—" : agent.printers.map((p) => p.name).join(", ")}
        </span>
        <span className="text-xs text-slate-400">
          Javio se {new Date(agent.last_seen_at).toLocaleString("sr-RS")}
        </span>
        {agent.online ? <StatusBadge label="Onlajn" tone="success" /> : <StatusBadge label="Offline" tone="neutral" />}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={agent.business_location_id ?? ""}
            onChange={(e) => assignLocation.mutate(e.target.value || null)}
            className="h-8 rounded-md border border-border px-2 text-xs focus:border-brand focus:outline-none"
            title="Poslovna lokacija"
          >
            <option value="">Nije dodeljena lokacija</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              if (confirm(`Opozvati agenta "${agent.name}"? Prestaje da štampa čim se sledeći put javi.`)) {
                revoke.mutate();
              }
            }}
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Opozovi agenta"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      {assignedLocation && draft && (
        <div className="mt-3">
          <AgentAutoPrintControls
            location={assignedLocation}
            draft={draft}
            onChange={(next) => onDraftChange(assignedLocation.id, next)}
            onSave={() => onSaveAutoPrint(assignedLocation.id, draft)}
            isSaving={isSavingAutoPrint}
          />
        </div>
      )}
      {!assignedLocation && (
        <p className="mt-2 text-xs text-slate-400">
          Dodelite poslovnu lokaciju da biste uključili automatsku štampu za ovaj agent.
        </p>
      )}
    </div>
  );
}

export function PrintAgentTab() {
  const queryClient = useQueryClient();
  const [showNewKey, setShowNewKey] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [autoPrintDrafts, setAutoPrintDrafts] = useState<Record<string, AutoPrintDraft>>({});

  const { data: keys, isLoading: keysLoading } = useQuery({
    queryKey: ["print-agent-keys"],
    queryFn: () => api.listPrintAgentKeys(),
  });
  const { data: agents, isLoading: agentsLoading } = useQuery({
    queryKey: ["print-agents"],
    queryFn: () => api.listPrintAgents(),
    // Agenti se javljaju na 10 min - osvežavanje na 30s je dovoljno da lista
    // "online" statusa deluje živo bez nepotrebnog opterećenja API-ja.
    refetchInterval: 30_000,
  });
  const { data: locations } = useQuery({
    queryKey: ["business-location-options"],
    queryFn: () => api.listBusinessLocationOptions(),
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) => api.revokePrintAgentKey(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["print-agent-keys"] }),
  });

  const updateAutoPrint = useMutation({
    mutationFn: ({ locationId, draft }: { locationId: string; draft: AutoPrintDraft }) =>
      api.updateBusinessLocationAutoPrint(locationId, {
        enabled: draft.enabled,
        printerRole: draft.printerRole,
        copies: draft.copies,
        thermalWidth: draft.thermalWidth,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["business-location-options"] }),
  });

  const draftFor = (locationId: string): AutoPrintDraft | undefined => {
    if (autoPrintDrafts[locationId]) return autoPrintDrafts[locationId];
    const loc = locations?.find((l) => l.id === locationId);
    return loc ? draftFromLocation(loc) : undefined;
  };

  const activeKeys = keys?.filter((k) => !k.revoked_at) ?? [];
  const agentsByKey = new Map<string, PrintAgentListItemDto[]>();
  const unassignedAgents: PrintAgentListItemDto[] = [];
  for (const agent of agents ?? []) {
    if (agent.print_agent_key_id) {
      const list = agentsByKey.get(agent.print_agent_key_id) ?? [];
      list.push(agent);
      agentsByKey.set(agent.print_agent_key_id, list);
    } else {
      unassignedAgents.push(agent);
    }
  }

  const isLoading = keysLoading || agentsLoading;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Print Agent</h2>
          <p className="text-xs text-slate-400">
            Štampa fiskalnih dokumenata sa kase na lokalni A4/termalni štampač preko Windows
            aplikacije instalirane u radnji. Svaki licencni ključ ima svoju karticu ispod - u njoj
            su agenti koji su se tim ključem prijavili, dodela lokacije i automatska štampa za tu
            lokaciju.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowNewKey(true)}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Novi ključ
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : activeKeys.length === 0 ? (
        <EmptyState
          icon={Printer}
          title="Nema licencnih ključeva"
          description='Napravite ključ i unesite ga u Print Agent (Licenca -> Licencni ključ) da bi se agent prijavio.'
        />
      ) : (
        <div className="space-y-4">
          {activeKeys.map((key) => {
            const keyAgents = agentsByKey.get(key.id) ?? [];
            return (
              <div key={key.id} className="rounded-xl border border-border bg-surface shadow-subtle">
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">{key.label || `Ključ ${key.key_prefix}…`}</h3>
                    <p className="text-xs text-slate-400">
                      Prefiks <span className="font-mono">{key.key_prefix}…</span> · Napravljen{" "}
                      {new Date(key.created_at).toLocaleString("sr-RS")} · Poslednja upotreba{" "}
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleString("sr-RS") : "nikad"}
                    </p>
                    <KeyReveal value={key.key} />
                  </div>
                  <button
                    onClick={() => {
                      if (
                        confirm(
                          `Opozvati ključ "${key.label || key.key_prefix}"? Svi agenti koji ga koriste prestaju da se javljaju.`,
                        )
                      ) {
                        revokeKey.mutate(key.id);
                      }
                    }}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Opozovi ključ"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                {keyAgents.length === 0 ? (
                  <p className="px-5 py-4 text-xs text-slate-400">
                    Još se nije javio nijedan agent sa ovim ključem. Unesite ključ u Print Agent na
                    Windows računaru (interfejs na{" "}
                    <span className="font-mono">http://127.0.0.1:8787</span>, deo „Licenca").
                  </p>
                ) : (
                  <div className="divide-y divide-border">
                    {keyAgents.map((agent) => (
                      <AgentRow
                        key={agent.id}
                        agent={agent}
                        locations={locations ?? []}
                        draft={agent.business_location_id ? draftFor(agent.business_location_id) : undefined}
                        onDraftChange={(locationId, next) =>
                          setAutoPrintDrafts((d) => ({ ...d, [locationId]: next }))
                        }
                        onSaveAutoPrint={(locationId, draft) => updateAutoPrint.mutate({ locationId, draft })}
                        isSavingAutoPrint={updateAutoPrint.isPending}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {unassignedAgents.length > 0 && (
        <div className="rounded-xl border border-border bg-surface shadow-subtle">
          <div className="border-b border-border px-5 py-4">
            <h3 className="text-sm font-semibold text-slate-800">Agenti bez prepoznatog ključa</h3>
            <p className="text-xs text-slate-400">
              Prijavili su se pre uvođenja grupisanja po ključu - povezuju se automatski na sledeće
              javljanje (ne treba ništa posebno uraditi).
            </p>
          </div>
          <div className="divide-y divide-border">
            {unassignedAgents.map((agent) => (
              <AgentRow
                key={agent.id}
                agent={agent}
                locations={locations ?? []}
                draft={agent.business_location_id ? draftFor(agent.business_location_id) : undefined}
                onDraftChange={(locationId, next) => setAutoPrintDrafts((d) => ({ ...d, [locationId]: next }))}
                onSaveAutoPrint={(locationId, draft) => updateAutoPrint.mutate({ locationId, draft })}
                isSavingAutoPrint={updateAutoPrint.isPending}
              />
            ))}
          </div>
        </div>
      )}

      {updateAutoPrint.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {updateAutoPrint.error instanceof Error ? updateAutoPrint.error.message : "Čuvanje nije uspelo."}
        </p>
      )}

      {showNewKey && (
        <NewKeyModal
          onClose={() => setShowNewKey(false)}
          onCreated={(rawKey) => {
            setShowNewKey(false);
            setRevealKey(rawKey);
          }}
        />
      )}
      {revealKey && <NewKeyRevealModal rawKey={revealKey} onClose={() => setRevealKey(null)} />}

    </div>
  );
}
